/**
 * Keyboard Timing Detection Strategy
 * Monitors typing in specific input fields
 * Analyzes keydown/keyup timing and micro-variations
 */
import { BaseStrategy } from '../strategy.js';
import { analyzeIntervals, scoreCoefficientOfVariation } from '../math-utils.js';
export class KeyboardStrategy extends BaseStrategy {
    constructor(options) {
        super();
        this.name = 'keyboard';
        this.defaultWeight = 0.10;
        this.events = [];
        this.targetSelectors = ['input[type="text"]', 'input[type="email"]', 'textarea'];
        this.focusedElement = null;
        this.focusedElementSelector = '';
        this.lastEventTimestamp = 0;
        this.sessionPauseThreshold = 1000;
        this.downListener = null;
        this.upListener = null;
        this.focusListeners = new Map();
        this.blurListeners = new Map();
        this.isActive = false;
        if (options === null || options === void 0 ? void 0 : options.targetSelectors) {
            this.targetSelectors = options.targetSelectors;
        }
    }
    start() {
        if (this.isActive)
            return;
        this.isActive = true;
        // Attach focus/blur listeners to all matching elements
        this.attachFocusListeners();
        // Key event listeners (only track when an input is focused)
        this.downListener = (e) => {
            if (!this.focusedElement)
                return; // No input focused
            const now = Date.now();
            const keyEvent = e;
            // Clear events if pause > 1s (new typing session)
            if (this.lastEventTimestamp > 0 && now - this.lastEventTimestamp > this.sessionPauseThreshold) {
                this.events = [];
            }
            this.events.push({
                key: keyEvent.key,
                type: 'down',
                timestamp: now,
                targetElement: this.focusedElementSelector,
            });
            // Notify detector - keystrokes are valuable events
            this.notifyEvent(0.8);
            this.lastEventTimestamp = now;
        };
        this.upListener = (e) => {
            if (!this.focusedElement)
                return; // No input focused
            const now = Date.now();
            const keyEvent = e;
            // Clear events if pause > 1s (new typing session)
            if (this.lastEventTimestamp > 0 && now - this.lastEventTimestamp > this.sessionPauseThreshold) {
                this.events = [];
            }
            this.events.push({
                key: keyEvent.key,
                type: 'up',
                timestamp: now,
                targetElement: this.focusedElementSelector,
            });
            this.lastEventTimestamp = now;
        };
        document.addEventListener('keydown', this.downListener);
        document.addEventListener('keyup', this.upListener);
    }
    /**
     * Add a new target selector at runtime
     */
    addTarget(selector) {
        if (!this.targetSelectors.includes(selector)) {
            this.targetSelectors.push(selector);
            if (this.isActive) {
                this.attachFocusListenersForSelector(selector);
            }
        }
    }
    attachFocusListeners() {
        this.targetSelectors.forEach(selector => {
            this.attachFocusListenersForSelector(selector);
        });
    }
    attachFocusListenersForSelector(selector) {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
            if (this.focusListeners.has(element))
                return; // Already attached
            const focusListener = () => {
                this.focusedElement = element;
                this.focusedElementSelector = selector;
            };
            const blurListener = () => {
                this.focusedElement = null;
                this.focusedElementSelector = '';
            };
            element.addEventListener('focus', focusListener);
            element.addEventListener('blur', blurListener);
            this.focusListeners.set(element, focusListener);
            this.blurListeners.set(element, blurListener);
        });
    }
    stop() {
        if (!this.isActive)
            return;
        this.isActive = false;
        if (this.downListener) {
            document.removeEventListener('keydown', this.downListener);
            this.downListener = null;
        }
        if (this.upListener) {
            document.removeEventListener('keyup', this.upListener);
            this.upListener = null;
        }
        // Remove focus/blur listeners
        this.focusListeners.forEach((listener, element) => {
            element.removeEventListener('focus', listener);
        });
        this.focusListeners.clear();
        this.blurListeners.forEach((listener, element) => {
            element.removeEventListener('blur', listener);
        });
        this.blurListeners.clear();
        this.focusedElement = null;
    }
    reset() {
        this.events = [];
        this.focusedElement = null;
        this.focusedElementSelector = '';
        this.lastEventTimestamp = 0;
    }
    score() {
        if (this.events.length < 6)
            return undefined; // Need at least 3 key pairs
        const downEvents = this.events.filter(e => e.type === 'down');
        if (downEvents.length < 3)
            return undefined;
        let score = 0;
        let factors = 0;
        // 1. Analyze keystroke intervals (time between keydown events)
        const keystrokeIntervals = [];
        for (let i = 1; i < downEvents.length; i++) {
            keystrokeIntervals.push(downEvents[i].timestamp - downEvents[i - 1].timestamp);
        }
        const keystrokeAnalysis = analyzeIntervals(keystrokeIntervals);
        if (keystrokeAnalysis) {
            const { statistics, allIdentical } = keystrokeAnalysis;
            // Keyboard-specific heuristics for keystroke timing
            if (allIdentical) {
                return 0.1; // All identical - bot!
            }
            const keystrokeScore = scoreCoefficientOfVariation(statistics.cv);
            score += keystrokeScore;
            factors++;
            // Early return if bot detected
            if (keystrokeScore <= 0.1)
                return keystrokeScore;
        }
        // 2. Analyze press durations (keydown-to-keyup duration)
        const pressDurations = [];
        for (let i = 0; i < this.events.length - 1; i++) {
            if (this.events[i].type === 'down' && this.events[i + 1].type === 'up' &&
                this.events[i].key === this.events[i + 1].key) {
                pressDurations.push(this.events[i + 1].timestamp - this.events[i].timestamp);
            }
        }
        const pressDurationAnalysis = analyzeIntervals(pressDurations);
        if (pressDurationAnalysis) {
            const { statistics, allIdentical } = pressDurationAnalysis;
            // Keyboard-specific heuristics for press duration
            if (allIdentical) {
                return 0.1; // All identical - bot!
            }
            // Check for suspiciously fast key releases (bots often have <5ms press duration)
            if (statistics.mean < 5) {
                score += 0.1; // Too fast = bot (instant release)
                factors++;
            }
            else {
                const pressDurationScore = scoreCoefficientOfVariation(statistics.cv);
                score += pressDurationScore;
                factors++;
                // Early return if bot detected
                if (pressDurationScore <= 0.1)
                    return pressDurationScore;
            }
        }
        // 3. Bonus for backspace usage (human error correction)
        const backspaceCount = this.events.filter(e => e.key === 'Backspace').length;
        if (backspaceCount > 0) {
            // Natural human behavior - making corrections
            const backspaceRatio = backspaceCount / downEvents.length;
            if (backspaceRatio > 0.05 && backspaceRatio < 0.3) {
                score += 1.0; // Reasonable error correction
                factors++;
            }
            else if (backspaceRatio > 0) {
                score += 0.8; // Some backspaces
                factors++;
            }
        }
        return factors > 0 ? score / factors : undefined;
    }
    getDebugInfo() {
        // Calculate press durations for visualization
        const pressDurations = [];
        for (let i = 0; i < this.events.length - 1; i++) {
            if (this.events[i].type === 'down' && this.events[i + 1].type === 'up' &&
                this.events[i].key === this.events[i + 1].key) {
                pressDurations.push(this.events[i + 1].timestamp - this.events[i].timestamp);
            }
        }
        return {
            eventCount: this.events.length,
            downEvents: this.events.filter(e => e.type === 'down').length,
            upEvents: this.events.filter(e => e.type === 'up').length,
            backspaceCount: this.events.filter(e => e.key === 'Backspace').length,
            pressDurations, // For graphing
            focusedElement: this.focusedElementSelector,
            trackedElements: this.focusListeners.size,
        };
    }
}
